import React, { useCallback, useEffect, useMemo, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import userApi from "../../HTTP/user";
import { getArticleById, getArticlesByAuthorId, getReviewById, getUserReviewsPaginated } from "../../HTTP/articles";
import { goToStaticPage } from "../../Helper/utils";
import { FEED_REPORT_REASONS } from "../Common/feedReportReasons";

const MAX_POST_LENGTH = 10000;
const MAX_COMMENT_LENGTH = 5000;
const MAX_REPORT_COMMENT_LENGTH = 1000;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const validateImageFile = (file) => {
  if (!file.type.startsWith("image/")) return "Le fichier choisi n'est pas une image.";
  if (file.size > MAX_IMAGE_BYTES) return "L'image est trop volumineuse (2 Mo maximum).";
  return "";
};

const getErrorMessage = (err) => err?.message || "Une erreur est survenue.";

const stripHtml = (value) =>
  typeof value === "string" ? value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";

const getSenderName = (sender) =>
  `${sender?.firstname || ""} ${sender?.lastname || ""}`.trim() || "Utilisateur";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("fr-FR");
};

const attachmentNameCache = new Map();

const AttachmentLink = ({ type, id, initialName }) => {
  const isArticle = type === "article";
  const cacheKey = `${type}:${id}`;
  const [name, setName] = useState(initialName || attachmentNameCache.get(cacheKey) || "");
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    if (initialName || attachmentNameCache.has(cacheKey)) return undefined;
    let active = true;
    (isArticle ? getArticleById(id) : getReviewById(id))
      .then((res) => {
        const entity = res?.article || res?.review || res?.rows?.[0] || res;
        const label = isArticle ? entity?.title : entity?.theme || entity?.title;
        if (label) {
          attachmentNameCache.set(cacheKey, label);
          if (active) setName(label);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [cacheKey, id, isArticle, initialName]);

  const open = async () => {
    if (opening) return;
    setOpening(true);
    try {
      await goToStaticPage(isArticle, id);
    } finally {
      setOpening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={opening}
      className="flex w-full items-center gap-2 mt-2 p-2 border border-gray-300 rounded text-left bg-white hover:bg-gray-50 disabled:opacity-60"
    >
      <span className="text-xs font-semibold uppercase text-zemusbis whitespace-nowrap">
        {isArticle ? "Article" : "Revue de presse"}
      </span>
      <span className="flex-1 font-semibold break-words">
        {name || `${isArticle ? "Article" : "Revue"} #${id}`}
      </span>
      <span aria-hidden="true" className="text-zemusbis">→</span>
    </button>
  );
};

const ErrorBanner = ({ message, onClose }) =>
  message ? (
    <div role="alert" className="mb-3 p-3 rounded bg-red-100 text-red-700 flex items-start">
      <span className="flex-1">{message}</span>
      {onClose && (
        <button type="button" onClick={onClose} aria-label="Fermer" className="ml-2 font-bold">
          ×
        </button>
      )}
    </div>
  ) : null;

const PostComments = ({ profileUserId, post, canModerate, currentUserId, onCountChange }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    userApi
      .getProfileFeedComments(profileUserId, post.id)
      .then((res) => active && setComments(res))
      .catch((err) => active && setError(getErrorMessage(err)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [profileUserId, post.id]);

  const submit = async (event) => {
    event.preventDefault();
    const cleaned = text.trim();
    if (!cleaned) return;
    if (cleaned.length > MAX_COMMENT_LENGTH) {
      setError(`Le commentaire ne peut pas dépasser ${MAX_COMMENT_LENGTH} caractères.`);
      return;
    }
    setSending(true);
    setError("");
    try {
      await userApi.postProfileFeedComment(profileUserId, post.id, cleaned);
      setText("");
      setComments(await userApi.getProfileFeedComments(profileUserId, post.id));
      onCountChange();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const remove = async (commentId) => {
    if (!window.confirm("Supprimer ce commentaire ?")) return;
    setError("");
    try {
      await userApi.deleteProfileFeedComment(profileUserId, post.id, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      onCountChange();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="mt-3 border-t pt-3">
      <ErrorBanner message={error} onClose={() => setError("")} />
      {loading ? (
        <p className="text-sm">Chargement des commentaires...</p>
      ) : comments.length === 0 ? (
        <p className="text-sm text-gray-500">Aucun commentaire.</p>
      ) : (
        <ul className="space-y-2">
          {comments.map((c) => {
            const author = c.sender || c.user || c.author || c;
            const authorId = Number(c.sender_id || c.user_id || author.id);
            const canDelete = canModerate || authorId === Number(currentUserId);
            return (
              <li key={c.id} className="bg-gray-50 rounded p-2">
                <div className="flex items-center text-sm">
                  <img
                    className="w-6 h-6 rounded-full mr-2"
                    src={author.profile_picture || process.env.PUBLIC_URL + "/profile_default.png"}
                    alt="commenter_profile_picture"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = process.env.PUBLIC_URL + "/profile_default.png";
                    }}
                  />
                  <span className="font-semibold notranslate">{getSenderName(author)}</span>
                  <span className="ml-2 text-gray-500">{formatDate(c.created_at)}</span>
                  {canDelete && (
                    <button
                      type="button"
                      aria-label="Supprimer le commentaire"
                      title="Supprimer le commentaire"
                      className="ml-auto text-red-600"
                      onClick={() => remove(c.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                  )}
                </div>
                <p className="whitespace-pre-wrap break-words">{c.comment ?? c.message}</p>
              </li>
            );
          })}
        </ul>
      )}
      <form onSubmit={submit} className="mt-2 flex flex-col gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={MAX_COMMENT_LENGTH}
          className="w-full border border-gray-300 rounded p-2"
          placeholder="Ajouter un commentaire..."
          aria-label="Ajouter un commentaire"
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={sending || text.trim().length === 0}
            className="bg-zemusbis hover:bg-zemushover text-white font-bold py-1 px-3 rounded disabled:opacity-60"
          >
            {sending ? "Envoi..." : "Commenter"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ProfileFeedSection = ({
  profileUserId,
  currentUserId,
  canView = true,
  canPost = false,
  title = "Fil d'actualité",
  scrollable = false,
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageVersions, setImageVersions] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [attachment, setAttachment] = useState(""); // "article:ID" | "review:ID" | ""
  const [attachmentLabel, setAttachmentLabel] = useState("");
  const [picker, setPicker] = useState(null); // "article" | "review" | null
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [openComments, setOpenComments] = useState({});
  const [reportMenuMessageId, setReportMenuMessageId] = useState(null);
  const [reportDialogMessage, setReportDialogMessage] = useState(null);
  const [reportReasonIndex, setReportReasonIndex] = useState("");
  const [reportComment, setReportComment] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState("");

  const normalizedCurrentUserId = useMemo(() => Number(currentUserId), [currentUserId]);
  const normalizedProfileUserId = useMemo(() => Number(profileUserId), [profileUserId]);
  const isOwner =
    canPost && normalizedCurrentUserId > 0 && normalizedCurrentUserId === normalizedProfileUserId;

  const canDeleteMessage = useCallback(
    (item) => {
      if (!item) return false;
      const senderId = Number(item.sender_id || item.sender?.id);
      return (
        senderId === normalizedCurrentUserId ||
        normalizedCurrentUserId === normalizedProfileUserId
      );
    },
    [normalizedCurrentUserId, normalizedProfileUserId]
  );

  const loadMessages = useCallback(async () => {
    if (!profileUserId || !canView) return;
    setLoading(true);
    setError("");
    try {
      setMessages(await userApi.getProfileFeed(profileUserId));
    } catch (err) {
      setMessages([]);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [profileUserId, canView]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const openPicker = async (type) => {
    setPicker(type);
    setPickerItems([]);
    setPickerError("");
    setPickerLoading(true);
    try {
      const res =
        type === "article"
          ? await getArticlesByAuthorId(profileUserId, 0, 100, 1, "")
          : await getUserReviewsPaginated(profileUserId, 0, 100, 1, "");
      setPickerItems(Array.isArray(res) ? res : Array.isArray(res?.rows) ? res.rows : []);
    } catch (err) {
      setPickerError(getErrorMessage(err));
    } finally {
      setPickerLoading(false);
    }
  };

  const updatePost = (messageId, patch) =>
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, ...patch } : m)));

  const refreshReactions = useCallback(
    async (messageId) => {
      try {
        const res = await userApi.getProfileFeedReactions(profileUserId, messageId);
        if (!res) return;
        const patch = {};
        if (res.likes_count !== undefined) patch.likes_count = res.likes_count;
        if (res.comments_count !== undefined) patch.comments_count = res.comments_count;
        if (res.liked_by_me !== undefined) patch.liked_by_me = res.liked_by_me;
        updatePost(messageId, patch);
      } catch (err) {
        setError(getErrorMessage(err));
      }
    },
    [profileUserId]
  );

  useEffect(() => {
    if (!imageFile) {
      setImagePreview("");
      return undefined;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const problem = validateImageFile(file);
    if (problem) {
      setFormError(problem);
      return;
    }
    setFormError("");
    setImageFile(file);
  };

  const handleReplaceImage = async (item, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const problem = validateImageFile(file);
    if (problem) {
      setError(problem);
      return;
    }
    setUploadingId(item.id);
    setError("");
    try {
      const res = await userApi.uploadProfileFeedImage(profileUserId, item.id, file);
      const url = res && typeof res === "object" ? res.featured_image : undefined;
      if (url) updatePost(item.id, { featured_image: url });
      else await loadMessages();
      setImageVersions((prev) => ({ ...prev, [item.id]: Date.now() }));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setUploadingId(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isOwner) return;

    const cleanedMessage = newMessage.trim();
    const [attachType, attachId] = attachment ? attachment.split(":") : [];

    if (!cleanedMessage && !imageFile && !attachId) {
      setFormError("Renseignez au moins un texte, une image ou un article / une revue de presse.");
      return;
    }
    if (cleanedMessage.length > MAX_POST_LENGTH) {
      setFormError(`Le texte ne peut pas dépasser ${MAX_POST_LENGTH} caractères.`);
      return;
    }

    setPosting(true);
    setFormError("");
    try {
      await userApi.postProfileFeedMessage(profileUserId, {
        message: cleanedMessage,
        image: imageFile,
        article_id: attachType === "article" ? Number(attachId) : undefined,
        review_id: attachType === "review" ? Number(attachId) : undefined,
      });
      setNewMessage("");
      setImageFile(null);
      setAttachment("");
      setAttachmentLabel("");
      await loadMessages();
    } catch (err) {
      setFormError(getErrorMessage(err));
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (!profileUserId || !messageId) return;
    if (!window.confirm("Supprimer ce message du fil ?")) return;

    try {
      await userApi.deleteProfileFeedMessage(profileUserId, messageId);
      setMessages((prev) => prev.filter((item) => item.id !== messageId));
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleReport = async (event) => {
    event.preventDefault();
    if (!reportDialogMessage || reportReasonIndex === "") return;

    const reason = FEED_REPORT_REASONS[Number(reportReasonIndex)];
    const additionalComment = reportComment.trim();
    const comment = `Motif détaillé : ${reason.label}${additionalComment ? `\nPrécisions : ${additionalComment}` : ""}`;
    if (comment.length > MAX_REPORT_COMMENT_LENGTH) {
      setError("Le motif et les précisions ne peuvent pas dépasser 1000 caractères.");
      return;
    }

    setReportSubmitting(true);
    setError("");
    setReportSuccess("");
    try {
      await userApi.reportProfileFeedMessage(
        profileUserId,
        reportDialogMessage.id,
        reason.value,
        comment
      );
      setReportDialogMessage(null);
      setReportReasonIndex("");
      setReportComment("");
      setReportSuccess("Votre signalement a été transmis. Merci.");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setReportSubmitting(false);
    }
  };

  const toggleLike = async (item) => {
    const liked = Boolean(item.liked_by_me);
    const previous = { liked_by_me: liked, likes_count: Number(item.likes_count) || 0 };
    updatePost(item.id, {
      liked_by_me: !liked,
      likes_count: Math.max(0, previous.likes_count + (liked ? -1 : 1)),
    });
    try {
      if (liked) await userApi.unlikeProfileFeedMessage(profileUserId, item.id);
      else await userApi.likeProfileFeedMessage(profileUserId, item.id);
      await refreshReactions(item.id);
    } catch (err) {
      updatePost(item.id, previous);
      setError(getErrorMessage(err));
    }
  };

  if (!canView) {
    return (
      <div className="profileContainer">
        <div className="ml-4 mr-4 mb-4 bg-white rounded-lg shadow p-4">
          <p>Vous devez etre ami avec cet utilisateur pour voir son fil d'actualité.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`profileContainer${scrollable ? " h-[60vh] overflow-y-scroll" : ""}`}>
      <div className="flex justify-center text-center mb-4">
        <h2 className="mt-2 text-2xl font-bold">{title}</h2>
      </div>

      {isOwner && (
        <form onSubmit={handleSubmit} className="ml-4 mr-4 mb-4 bg-white rounded-lg shadow p-4">
          <ErrorBanner message={formError} onClose={() => setFormError("")} />
          <label htmlFor="profile-feed-message" className="block mb-2 font-semibold">
            Publier sur votre fil
          </label>
          <textarea
            id="profile-feed-message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="w-full border border-gray-300 rounded p-2 min-h-[90px]"
            placeholder="Texte (facultatif)..."
            maxLength={MAX_POST_LENGTH}
          />
          <label htmlFor="profile-feed-image" className="block mt-3 mb-1 font-semibold">
            Afficher une image (facultatif, 2 Mo maximum)
          </label>
          <input
            id="profile-feed-image"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="w-full"
          />
          {imagePreview && (
            <div className="mt-2">
              <img src={imagePreview} alt="Aperçu" className="max-h-40 rounded" />
              <button
                type="button"
                className="mt-1 text-red-600 text-sm"
                onClick={() => setImageFile(null)}
              >
                Retirer l'image
              </button>
            </div>
          )}
          <p className="mt-3 mb-1 font-semibold">Article ou revue de presse (facultatif)</p>
          {attachment ? (
            <div className="flex items-center gap-2 p-2 border border-gray-300 rounded">
              <span className="flex-1 break-words">
                {attachment.startsWith("article:") ? "Article : " : "Revue de presse : "}
                {attachmentLabel}
              </span>
              <button
                type="button"
                className="text-red-600 text-sm"
                onClick={() => {
                  setAttachment("");
                  setAttachmentLabel("");
                }}
              >
                Retirer
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => openPicker("article")}
                className="border border-zemusbis text-zemusbis font-semibold py-1 px-3 rounded"
              >
                Ajouter un article
              </button>
              <button
                type="button"
                onClick={() => openPicker("review")}
                className="border border-zemusbis text-zemusbis font-semibold py-1 px-3 rounded"
              >
                Ajouter une revue de presse
              </button>
            </div>
          )}
          {picker && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
              role="dialog"
              aria-modal="true"
              onClick={() => setPicker(null)}
            >
              <div
                className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center p-4 border-b">
                  <h3 className="text-lg font-bold flex-1">
                    {picker === "article" ? "Choisir un article" : "Choisir une revue de presse"}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setPicker(null)}
                    aria-label="Fermer"
                    className="text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>
                <div className="overflow-y-auto p-4">
                  {pickerLoading ? (
                    <p>Chargement...</p>
                  ) : pickerError ? (
                    <ErrorBanner message={pickerError} />
                  ) : pickerItems.length === 0 ? (
                    <p>
                      {picker === "article"
                        ? "Aucun article publié."
                        : "Aucune revue de presse publiée."}
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {pickerItems.map((entry) => {
                        const label =
                          picker === "article"
                            ? entry.title
                            : entry.theme || entry.title || `Revue #${entry.id}`;
                        const text = stripHtml(
                          picker === "article" ? entry.description : entry.presentation
                        );
                        return (
                          <li key={entry.id}>
                            <button
                              type="button"
                              className="w-full text-left p-3 border rounded hover:bg-gray-50"
                              onClick={() => {
                                setAttachment(`${picker}:${entry.id}`);
                                setAttachmentLabel(label);
                                setPicker(null);
                              }}
                            >
                              <span className="block font-semibold">{label}</span>
                              {text && (
                                <span className="block text-sm text-gray-600 line-clamp-2">
                                  {text.length > 160 ? `${text.slice(0, 160)}…` : text}
                                </span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end mt-3">
            <button
              type="submit"
              disabled={posting}
              className="bg-zemusbis hover:bg-zemushover text-white font-bold py-2 px-4 rounded disabled:opacity-60"
            >
              {posting ? "Publication..." : "Publier"}
            </button>
          </div>
        </form>
      )}

      <div className="ml-4 mr-4">
        <ErrorBanner message={error} onClose={() => setError("")} />
        {reportSuccess && (
          <div role="status" className="mb-3 p-3 rounded bg-green-100 text-green-800 flex items-start">
            <span className="flex-1">{reportSuccess}</span>
            <button
              type="button"
              onClick={() => setReportSuccess("")}
              aria-label="Fermer"
              className="ml-2 font-bold"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="ml-4 mb-4">Chargement du fil d'actualité...</p>
      ) : messages.length === 0 && !error ? (
        <div className="ml-4 mr-4 mb-4 bg-white rounded-lg shadow p-4">
          <p>Aucun message pour le moment.</p>
        </div>
      ) : (
        <div className="ml-4 mr-4 mb-4 space-y-3">
          {messages.map((item) => (
            <div
              key={item.id}
              className="relative rounded-lg shadow p-4"
              style={{ backgroundColor: item.article_id || item.review_id ? "#dff1f7" : "#fff" }}
            >
              <div className="flex items-center mb-2">
                <img
                  className="w-8 h-8 rounded-full mr-2"
                  src={item.sender?.profile_picture || process.env.PUBLIC_URL + "/profile_default.png"}
                  alt="sender_profile_picture"
                />
                <div>
                  <p className="font-semibold notranslate">{getSenderName(item.sender)}</p>
                  <p className="text-xs text-gray-500">{formatDate(item.created_at)}</p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {canDeleteMessage(item) && (
                    <button
                      type="button"
                      aria-label="Supprimer le message"
                      title="Supprimer le message"
                      className="text-red-600 font-semibold"
                      onClick={() => handleDelete(item.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                  )}
                  {Number(item.sender_id || item.sender?.id) !== normalizedCurrentUserId && (
                    <button
                      type="button"
                      aria-label="Autres actions sur la publication"
                      aria-haspopup="menu"
                      aria-expanded={reportMenuMessageId === item.id}
                      title="Autres actions"
                      className="text-gray-600"
                      onClick={() => setReportMenuMessageId((id) => id === item.id ? null : item.id)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </button>
                  )}
                  {reportMenuMessageId === item.id && (
                    <div role="menu" className="absolute right-3 top-12 z-10 min-w-48 rounded border bg-white py-1 shadow-lg">
                      <button
                        type="button"
                        role="menuitem"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                        onClick={() => {
                          setReportDialogMessage(item);
                          setReportMenuMessageId(null);
                          setReportReasonIndex("");
                          setReportComment("");
                          setError("");
                          setReportSuccess("");
                        }}
                      >
                        Signaler la publication
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {item.message && <p className="whitespace-pre-wrap break-words">{item.message}</p>}
              {item.review_id && (
                <AttachmentLink
                  type="review"
                  id={item.review_id}
                  initialName={item.review?.theme || item.review_theme || item.review_title}
                />
              )}
              {item.article_id && (
                <AttachmentLink
                  type="article"
                  id={item.article_id}
                  initialName={item.article?.title || item.article_title}
                />
              )}
              {item.featured_image && (
                <img
                  src={
                    imageVersions[item.id]
                      ? `${item.featured_image}${item.featured_image.includes("?") ? "&" : "?"}v=${imageVersions[item.id]}`
                      : item.featured_image
                  }
                  alt="Illustration du message"
                  className="block mx-auto mt-2 w-full max-w-[690px] h-auto rounded"
                />
              )}
              {isOwner && (
                <label className="inline-block mt-2 text-sm text-zemusbis font-semibold cursor-pointer underline">
                  {uploadingId === item.id
                    ? "Envoi de l'image..."
                    : item.featured_image
                      ? "Remplacer l'image"
                      : "Ajouter une image"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingId === item.id}
                    onChange={(e) => handleReplaceImage(item, e)}
                  />
                </label>
              )}

              <div className="flex items-center gap-4 mt-3 text-sm">
                <button
                  type="button"
                  onClick={() => toggleLike(item)}
                  aria-pressed={Boolean(item.liked_by_me)}
                  className={item.liked_by_me ? "font-bold text-zemusbis" : "text-gray-600"}
                >
                  {item.liked_by_me ? "♥" : "♡"} J'aime ({Number(item.likes_count) || 0})
                </button>
                <button
                  type="button"
                  className="text-gray-600"
                  onClick={() => setOpenComments((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                >
                  Commentaires ({Number(item.comments_count) || 0})
                </button>
              </div>

              {openComments[item.id] && (
                <PostComments
                  profileUserId={profileUserId}
                  post={item}
                  currentUserId={currentUserId}
                  canModerate={normalizedCurrentUserId === normalizedProfileUserId}
                  onCountChange={() => refreshReactions(item.id)}
                />
              )}
            </div>
          ))}
        </div>
      )}
      {reportDialogMessage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          role="presentation"
          onClick={() => !reportSubmitting && setReportDialogMessage(null)}
        >
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-feed-report-title"
            onSubmit={handleReport}
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
          >
            <h3 id="profile-feed-report-title" className="mb-2 text-xl font-bold">
              Signaler la publication
            </h3>
            <p className="mb-4 text-sm text-gray-600">
              Choisissez le motif qui décrit le mieux le problème.
            </p>
            <fieldset className="space-y-2">
              <legend className="sr-only">Motif du signalement</legend>
              {FEED_REPORT_REASONS.map((reason, index) => (
                <label key={reason.label} className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="profile-feed-report-reason"
                    value={index}
                    checked={reportReasonIndex === String(index)}
                    onChange={(event) => setReportReasonIndex(event.target.value)}
                    required
                    className="mt-1"
                  />
                  <span>{reason.label}</span>
                </label>
              ))}
            </fieldset>
            <label htmlFor="profile-feed-report-comment" className="mt-4 block text-sm font-semibold">
              Précisions (facultatif)
            </label>
            <textarea
              id="profile-feed-report-comment"
              value={reportComment}
              onChange={(event) => setReportComment(event.target.value)}
              maxLength={Math.max(
                0,
                MAX_REPORT_COMMENT_LENGTH -
                  (reportReasonIndex === "" ? 0 : `Motif détaillé : ${FEED_REPORT_REASONS[Number(reportReasonIndex)].label}`.length + "\nPrécisions : ".length)
              )}
              rows={3}
              className="mt-1 w-full rounded border border-gray-300 p-2"
              placeholder="Ajoutez des informations utiles à l’équipe de modération."
            />
            {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                disabled={reportSubmitting}
                onClick={() => setReportDialogMessage(null)}
                className="rounded border px-4 py-2 disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={reportSubmitting || reportReasonIndex === ""}
                className="rounded bg-zemusbis px-4 py-2 font-semibold text-white disabled:opacity-60"
              >
                {reportSubmitting ? "Envoi..." : "Envoyer le signalement"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProfileFeedSection;
